import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Spinner } from "@/components/shared";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Mail, Trash2, Check, Circle, Inbox } from "lucide-react";
import { toast } from "sonner";

const TOPIC_LABEL = {
  general: "General question", billing: "Billing & account", technical: "Technical issue",
  partnership: "Partnership/media", privacy: "Privacy request",
};
const STATUS_STYLE = {
  new: "bg-kid-teal/12 text-kid-teal", read: "bg-kid-mist text-kid-muted", resolved: "bg-kid-green/15 text-[#3f9d5c]",
};
const FILTERS = [{ v: "", l: "All" }, { v: "new", l: "New" }, { v: "read", l: "Read" }, { v: "resolved", l: "Resolved" }];
const fmt = (iso) => { try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

export default function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [deleting, setDeleting] = useState(null);

  const load = async (status = filter) => {
    const { data } = await api.get("/admin/messages", { params: status ? { status } : {} });
    setMessages(data.messages); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (m, status) => {
    try {
      await api.patch(`/admin/messages/${m.id}`, { status });
      setMessages((ms) => ms.map((x) => x.id === m.id ? { ...x, status } : x));
      if (filter && status !== filter) setMessages((ms) => ms.filter((x) => x.id !== m.id));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doDelete = async () => {
    try { await api.delete(`/admin/messages/${deleting.id}`); toast.success("Message deleted."); setDeleting(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const changeFilter = (v) => { setFilter(v); setLoading(true); load(v); };

  if (loading) return <Spinner />;

  return (
    <div>
      <PageHeader eyebrow="Support" title="Messages" subtitle="Contact form submissions from families and visitors." />

      <div className="flex gap-2 mb-4">
        {FILTERS.map((f) => (
          <button key={f.v} onClick={() => changeFilter(f.v)}
            className={`text-sm font-semibold px-3.5 py-1.5 rounded-full transition-colors ${filter === f.v ? "bg-kid-teal text-white" : "bg-white text-kid-muted border border-kid-line hover:bg-kid-mist"}`}
            data-testid={`messages-filter-${f.v || "all"}`}>
            {f.l}
          </button>
        ))}
      </div>

      {messages.length === 0 ? (
        <div className="kid-card p-10 text-center text-kid-muted" data-testid="messages-empty">
          <Inbox size={28} className="mx-auto mb-3 opacity-60" />
          No messages{filter ? ` with status “${filter}”` : ""} yet.
        </div>
      ) : (
        <div className="space-y-3" data-testid="messages-list">
          {messages.map((m) => (
            <div key={m.id} className="kid-card p-5" data-testid="message-row">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLE[m.status] || ""}`} data-testid="message-status">{m.status}</span>
                    <span className="text-xs text-kid-muted">{TOPIC_LABEL[m.topic] || m.topic}</span>
                    <span className="text-xs text-kid-muted/70">· {fmt(m.created_at)}</span>
                  </div>
                  <div className="font-semibold text-kid-navy">{m.name}</div>
                  <a href={`mailto:${m.email}`} className="text-sm text-kid-teal hover:underline inline-flex items-center gap-1" data-testid="message-email"><Mail size={13} /> {m.email}</a>
                  <p className="text-sm text-kid-navy/90 mt-2 whitespace-pre-wrap">{m.message}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {m.status !== "read" && m.status !== "resolved" && (
                    <button onClick={() => setStatus(m, "read")} className="text-kid-muted p-1.5 rounded-lg hover:bg-kid-mist" title="Mark read" data-testid="message-mark-read"><Circle size={16} /></button>
                  )}
                  {m.status !== "resolved" && (
                    <button onClick={() => setStatus(m, "resolved")} className="text-[#3f9d5c] p-1.5 rounded-lg hover:bg-kid-mist" title="Mark resolved" data-testid="message-mark-resolved"><Check size={16} /></button>
                  )}
                  <button onClick={() => setDeleting(m)} className="text-kid-coral p-1.5 rounded-lg hover:bg-kid-mist" title="Delete" data-testid="message-delete"><Trash2 size={16} /></button>
                </div>
              </div>
              {m.status === "resolved" && (
                <button onClick={() => setStatus(m, "new")} className="text-xs text-kid-teal font-semibold mt-3" data-testid="message-reopen">Reopen</button>
              )}
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="message-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this message?</AlertDialogTitle>
            <AlertDialogDescription>The message from {deleting?.name} ({deleting?.email}) will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="message-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-kid-coral hover:bg-kid-coral/90" data-testid="message-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
